import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import EDButton from '../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDCheckbox from '../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDatePicker from '../../../../../../../../../BaseComponents/DatePicker/EDDatePicker';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import { BusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  isDateinLockedBokfPeriod,
  isDateinLockedBokfYear,
} from '../../Utils/BokforderPeriodDialogUtils';
import { IFieldValidation } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  compareDateString,
  diffDay,
  getMonth,
  getYear,
} from '../../../../../../../../../Utils/dateUtils';
import { BokforderWrapperContext } from '../../../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import BokforderPeriodSummeringInfoDialog from '../BokforderPeriodInfoDialog/BokforderPeriodInfoDialog';
import EDDialog from '../../../../../../../../../BaseComponents/Dialog/EDDialog';
import useCantEditPeriod from '../../../../Hooks/useCantEditPeriod';
import EDColumns, {
  EDColumn,
} from '../../../../../../../../../BaseComponents/Columns/EDColumns';
import { useIsSmallBreakpoint } from '../../../../../../../../../Components/BreakpointContext/BreakpointContextUtils';

export interface IDateInputValue {
  value: string;
  isValid: boolean;
}

interface IProps<T> {
  availableRows: T[];
  chosenRows: T[];
  clearButtonDisabledReason?: React.ReactNode;
  disableClearButton?: boolean;
  bokfDatumDisabled?: boolean;
  fromDateInputValue: IDateInputValue;
  infoDiffMoreThanOneYearDialogOpen: boolean;
  infoSummeringDialogOpen: boolean;
  onClearClick: () => void;
  onDateBlur: (
    inputSetter: React.Dispatch<React.SetStateAction<IDateInputValue>>
  ) => (
    event: React.FocusEvent<HTMLDivElement | HTMLInputElement>,
    newValue: string,
    isValidDate: boolean
  ) => void;
  onUseClick: () => void;
  setChosenRows: React.Dispatch<React.SetStateAction<T[]>>;
  setFromDateInputValue: React.Dispatch<React.SetStateAction<IDateInputValue>>;
  setInfoDiffMoreThanOneYearDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setInfoSummeringDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setToDateInputValue: React.Dispatch<React.SetStateAction<IDateInputValue>>;
  toDateInputValue: IDateInputValue;
}

const BokforderPeriodDateHandler: <T>(
  props: IProps<T>
) => React.ReactElement<any> = props => {
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const formContext = useContext(BusinessDataFormContext);
  const buttonRef = useRef<HTMLButtonElement>(undefined);
  const isSmall = useIsSmallBreakpoint();

  const [isEditingFromDate, setIsEditingFromDate] = useState<boolean>(false);
  const [isEditingToDate, setIsEditingToDate] = useState<boolean>(false);

  const bokforderDate = useMemo(
    () => new Date(formContext?.formState.currentValues?.BOKF_DATUM),
    [formContext?.formState.currentValues?.BOKF_DATUM]
  );

  const cantEditPeriod = useCantEditPeriod();

  const isUseButtonDisabled = useCallback((): IFieldValidation[] => {
    const errorMessages: IFieldValidation[] = [];

    if (cantEditPeriod) {
      const error: IFieldValidation = {
        fieldName: 'AnvandButton',
        message:
          'Du kan inte redigera bokföringsordern när den inte ligger hos dig',
        type: 'Error',
      };
      errorMessages.push(error);
      return errorMessages;
    }

    if (
      formContext?.formState.touched?.BOKF_DATUM &&
      isNaN(bokforderDate.valueOf())
    ) {
      const error: IFieldValidation = {
        fieldName: 'BOKF_DATUM',
        message: 'Bokf.datum är felaktigt',
        type: 'Error',
      };
      errorMessages.push(error);
    }
    // Vi sätter dagen på bokföringsdatumet till 1 för att vi bara behöver år och månad för validationen
    const bokfDate = new Date(bokforderDate.setDate(1));

    if (props.fromDateInputValue.value === '') {
      const error: IFieldValidation = {
        fieldName: 'fromPeriod',
        message: 'Fr o m datum saknas',
        type: 'MissingValue',
      };
      errorMessages.push(error);
    }

    if (props.toDateInputValue.value === '') {
      const error: IFieldValidation = {
        fieldName: 'tomPeriod',
        message: 'T o m datum saknas',
        type: 'MissingValue',
      };
      errorMessages.push(error);
    }

    if (
      formContext?.formState.currentValues?.BOKF_DATUM == null ||
      formContext?.formState.currentValues?.BOKF_DATUM === ''
    ) {
      return errorMessages;
    }
    let toDate = new Date(new Date(props.toDateInputValue.value).setDate(1));
    let fromDate = new Date(
      new Date(props.fromDateInputValue.value).setDate(1)
    );

    if (props.chosenRows.length === 0) {
      const error: IFieldValidation = {
        fieldName: 'AnvandButton',
        message: 'Inga rader är valda',
        type: 'Error',
      };
      errorMessages.push(error);
    }

    //Validering som först körs när man har matat in datumen i rätt format

    const toYear = getYear(toDate);
    const bokfMonth = getMonth(bokfDate);
    const toMonth = getMonth(toDate);
    const fromYear = getYear(fromDate);
    const bokfYear = getYear(bokfDate);
    const fromMonth = getMonth(fromDate);
    const diffBokfYearFromYear = bokfYear - fromYear;
    const diffBokfYearToYear = bokfYear - toYear;

    if (!isEditingToDate) {
      if (
        !props.toDateInputValue.isValid &&
        props.toDateInputValue.value !== ''
      ) {
        const error: IFieldValidation = {
          fieldName: 'tomPeriod',
          message: 'T o m datumet är felaktigt',
          type: 'Error',
        };
        errorMessages.push(error);
      }

      //Validera endast när man ej editerar t o m datum
      if (fromDate > toDate && props.fromDateInputValue.isValid) {
        const errorTodate: IFieldValidation = {
          fieldName: 'tomPeriod',
          message: 'T o m datumet är tidigare än fr o m datumet',
          type: 'Error',
        };

        errorMessages.push(errorTodate);
      }

      // Framåt i tiden
      if (toDate > bokfDate) {
        const bokfDatePlusFiveYears = new Date(
          getYear(bokfDate) + 5,
          getMonth(bokfDate),
          1
        );
        if (toDate > bokfDatePlusFiveYears && !isEditingToDate) {
          const error: IFieldValidation = {
            fieldName: 'tomPeriod',
            message:
              'Det är inte tillåtet att periodisera över mer än 60 månader',
            type: 'Error',
          };
          errorMessages.push(error);
        }
      }

      //Kan inte periodiersa under nuvarande period
      if (
        compareDateString(toDate, bokforderDate) === 0 &&
        props.toDateInputValue.isValid
      ) {
        const error: IFieldValidation = {
          fieldName: 'tomPeriod',
          message:
            'Det är inte tillåtet att periodisera på samma period som bokföringsdatumet',
          type: 'Error',
        };
        errorMessages.push(error);
      }

      // Bakåt i tiden
      if (toDate <= bokfDate && fromDate < bokfDate) {
        // Validation på från-period som är beroende på till-Period
        if (
          diffBokfYearFromYear != 1 &&
          (diffBokfYearFromYear != 0 || diffBokfYearToYear != 0)
        ) {
          if (diffBokfYearFromYear != 0 && props.fromDateInputValue.isValid) {
            const error: IFieldValidation = {
              fieldName: 'fromPeriod',
              message: 'Det går bara att periodisera till föregående år',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }

        //Samma år som bokfDatum
        if (diffBokfYearToYear === 0 && bokfMonth > 3) {
          if (bokfMonth - fromMonth != 1) {
            const error: IFieldValidation = {
              fieldName: 'fromPeriod',
              message: 'Man kan bara periodisera en månad bakåt under året',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }
        // Kan bara periodiesera till december förgående år
        if (bokfMonth <= 3) {
          if (diffBokfYearFromYear === 1 && fromMonth != 12) {
            const error: IFieldValidation = {
              fieldName: 'fromPeriod',
              message:
                'Periodisering till föregående år kan endast göras till period december',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }

        if (diffBokfYearToYear === 1 && bokfMonth > 3) {
          const error: IFieldValidation = {
            fieldName: 'tomPeriod',
            message:
              'Periodisering till föregående år kan endast göras under period jan-mars',
            type: 'Error',
          };
          errorMessages.push(error);
        }

        if (
          diffBokfYearFromYear != 1 &&
          (diffBokfYearFromYear != 0 || diffBokfYearToYear != 0)
        ) {
          if (diffBokfYearToYear != 0) {
            const error: IFieldValidation = {
              fieldName: 'tomPeriod',
              message: 'Det går bara att periodisera till föregående år',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }

        //Samma år som bokfDatum
        if (diffBokfYearToYear === 0 && bokfMonth > 3) {
          if (bokfMonth - toMonth > 1) {
            const error: IFieldValidation = {
              fieldName: 'tomPeriod',
              message: 'Man kan bara periodisera en månad bakåt under året',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }
        // Kan bara periodiesera till december förgående år
        if (bokfMonth <= 3) {
          if (diffBokfYearToYear === 1 && toMonth != 12) {
            const error: IFieldValidation = {
              fieldName: 'tomPeriod',
              message:
                'Periodisering till föregående år kan endast göras till period december',
              type: 'Error',
            };
            errorMessages.push(error);
          }
        }
      }

      // Låst år
      if (
        isDateinLockedBokfYear(toDate, bokforderWrapperContext.allBokfar) &&
        fromYear < bokfYear &&
        toYear < bokfYear
      ) {
        const error: IFieldValidation = {
          fieldName: 'tomPeriod',
          message: 'T o m datumet är i ett låst bokföringsår',
          type: 'Error',
        };
        errorMessages.push(error);
      } else {
        // Låst period
        if (
          isDateinLockedBokfPeriod(
            fromDate,
            bokforderWrapperContext.allBokfar
          ) &&
          fromDate < bokfDate &&
          toDate < bokfDate
        ) {
          const error: IFieldValidation = {
            fieldName: 'fromPeriod',
            message: 'Fr o m datumet är i en låst bokföringsperiod',
            type: 'Error',
          };
          errorMessages.push(error);
        }
        if (
          isDateinLockedBokfPeriod(toDate, bokforderWrapperContext.allBokfar) &&
          fromDate < bokfDate &&
          toDate < bokfDate
        ) {
          const error: IFieldValidation = {
            fieldName: 'tomPeriod',
            message: 'T o m datumet är i en låst bokföringsperiod',
            type: 'Error',
          };
          errorMessages.push(error);
        }
      }
    }

    if (!isEditingFromDate) {
      if (
        !isEditingFromDate &&
        !props.fromDateInputValue.isValid &&
        props.fromDateInputValue.value !== ''
      ) {
        const error: IFieldValidation = {
          fieldName: 'fromPeriod',
          message: 'Fr o m datumet är felaktigt',
          type: 'Error',
        };
        errorMessages.push(error);
      }

      if (diffBokfYearFromYear === 1 && bokfMonth > 3) {
        const error: IFieldValidation = {
          fieldName: 'fromPeriod',
          message:
            'Periodisering till föregående år kan endast göras under period jan-mars',
          type: 'Error',
        };
        errorMessages.push(error);
      }

      //Validera endast när man ej editerar fr o m datum
      if (fromDate > toDate && props.toDateInputValue.isValid) {
        const errorFromdate: IFieldValidation = {
          fieldName: 'fromPeriod',
          message: 'Fr o m datumet är senare än t o m datumet',
          type: 'Error',
        };
        errorMessages.push(errorFromdate);
      }

      // Låst år
      if (
        isDateinLockedBokfYear(fromDate, bokforderWrapperContext.allBokfar) &&
        fromYear < bokfYear &&
        toYear < bokfYear
      ) {
        const error: IFieldValidation = {
          fieldName: 'fromPeriod',
          message: 'Fr o m datumet är i ett låst bokföringsår',
          type: 'Error',
        };
        errorMessages.push(error);
      }
    }

    return errorMessages;
  }, [
    formContext?.formState.touched?.BOKF_DATUM,
    formContext?.formState.currentValues?.BOKF_DATUM,
    cantEditPeriod,
    bokforderDate,
    props.fromDateInputValue,
    props.toDateInputValue,
    props.chosenRows,
    isEditingToDate,
    isEditingFromDate,
    bokforderWrapperContext.allBokfar,
  ]);

  const dateInputErrors = useMemo(() => {
    const errors = isUseButtonDisabled();
    return [
      ...errors,
      ...(formContext?.formState.validation.filter(val =>
        val.fieldName.includes('BOKF_DATUM')
      ) ?? []),
    ];
  }, [formContext?.formState.validation, isUseButtonDisabled]);

  const getDateHelpText = useCallback(
    (dateInputKey: string) => {
      const errors = dateInputErrors.filter(error => {
        return error.fieldName === dateInputKey && error.type === 'Error'
          ? error.message
          : '';
      });
      if (errors.length > 0) {
        return errors
          .map(error => {
            return error.message;
          })
          .join('\n');
      } else {
        return '';
      }
    },
    [dateInputErrors]
  );

  const getBokfDatumHelpText = useCallback(() => {
    const formstateValidation = formContext?.formState.validation?.find(
      val => val.fieldName === 'BOKF_DATUM'
    );
    if (formstateValidation) {
      return formstateValidation.message;
    } else if (dateInputErrors.length > 0) {
      return getDateHelpText('BOKF_DATUM');
    }
    return undefined;
  }, [dateInputErrors, formContext?.formState.validation, getDateHelpText]);

  return (
    <>
      <EDColumns>
        <EDColumn sm={6} md={1.2} order={isSmall ? 2 : undefined}>
          <EDBox sx={{ height: 'auto', display: 'flex', alignItems: 'center' }}>
            <EDCheckbox
              my={!isSmall && 2}
              indeterminate={
                props.chosenRows.length > 0
                  ? props.chosenRows.length === props.availableRows.length
                    ? false
                    : true
                  : false
              }
              checked={props.availableRows.length === props.chosenRows.length}
              onCheck={isInputChecked => {
                if (isInputChecked) {
                  props.setChosenRows(props.availableRows);
                } else {
                  props.setChosenRows([]);
                }
              }}
              label={'Välj alla'}
              testId="valjAllaRaderPeriod"
            />
          </EDBox>
        </EDColumn>
        <EDColumn sm={6} md={2.8}>
          <EDDatePicker
            disabled={props.bokfDatumDisabled}
            key="BOKF_DATUM"
            disableFutureDates
            label={'Beställt Bokf.datum'}
            onChange={newValue => {
              formContext?.update([
                { operation: 'OwnFields', values: { BOKF_DATUM: newValue } },
              ]);
            }}
            onBlur={() => {
              formContext?.formStateDispatch({
                type: 'TOUCH',
                path: ['BOKF_DATUM'],
              });
            }}
            required
            size="ownblock"
            error={
              (formContext?.formState.validation?.filter(
                val =>
                  val.type === 'Error' &&
                  val.fieldName === 'BOKF_DATUM' &&
                  formContext?.formState.touched?.['BOKF_DATUM']
              )?.length ?? 0) > 0 ||
              dateInputErrors.filter(error => {
                return (
                  error.fieldName === 'BOKF_DATUM' && error.type === 'Error'
                );
              }).length > 0
            }
            helperText={getBokfDatumHelpText()}
            value={formContext?.formState.currentValues?.BOKF_DATUM}
            warning={
              (formContext?.formState.validation?.filter(
                val => val.type === 'Warning' && val.fieldName === 'BOKF_DATUM'
              )?.length ?? 0) > 0
            }
            testId="bokfDatumPeriod"
          />
        </EDColumn>
        <EDColumn sm={6} md={2.8}>
          <EDDatePicker
            error={
              dateInputErrors.filter(error => {
                return (
                  error.fieldName === 'fromPeriod' && error.type === 'Error'
                );
              }).length > 0
            }
            helperText={getDateHelpText('fromPeriod')}
            key="fromPeriod"
            label={'Period, fr o m'}
            onDateBlur={(
              event: React.FocusEvent<HTMLDivElement | HTMLInputElement>,
              newValue: string,
              isValidDate: boolean
            ) => {
              props.onDateBlur(props.setFromDateInputValue)(
                event,
                newValue,
                isValidDate
              );
              setIsEditingFromDate(false);
            }}
            onFocus={() => setIsEditingFromDate(true)}
            onDateChange={(newValue, isValid) => {
              props.setFromDateInputValue({
                value: newValue,
                isValid: isValid,
              });
            }}
            required
            size="ownblock"
            value={props.fromDateInputValue.value}
            testId="fromPeriod"
          />
        </EDColumn>
        <EDColumn sm={6} md={2.8}>
          <EDDatePicker
            error={
              dateInputErrors.filter(error => {
                return (
                  error.fieldName === 'tomPeriod' && error.type === 'Error'
                );
              }).length > 0
            }
            helperText={getDateHelpText('tomPeriod')}
            key="tomPeriod"
            label={'Period, t o m'}
            onFocus={() => setIsEditingToDate(true)}
            onDateBlur={(
              event: React.FocusEvent<HTMLDivElement | HTMLInputElement>,
              newValue: string,
              isValidDate: boolean
            ) => {
              props.onDateBlur(props.setToDateInputValue)(
                event,
                newValue,
                isValidDate
              );
              setIsEditingToDate(false);
              setTimeout(() => {
                buttonRef.current?.focus();
              }, 10);
            }}
            onDateChange={(newValue, isValid) => {
              props.setToDateInputValue({
                value: newValue,
                isValid: isValid,
              });
            }}
            required
            size="ownblock"
            value={props.toDateInputValue.value}
            testId="tomPeriod"
          />
        </EDColumn>
        <EDColumn sm={6} md={2.4}>
          <EDBox
            marginTop={isSmall ? 'auto' : 2}
            marginBottom={isSmall ? 'auto' : 1}
          >
            <EDButton
              key="AnvandButton"
              label="Använd"
              disabled={
                dateInputErrors.length > 0 ||
                isEditingFromDate ||
                isEditingToDate
              }
              disabledReason={dateInputErrors.map((error, index) => (
                <EDBox key={index}>{error.message}</EDBox>
              ))}
              buttonRef={buttonRef}
              mr={2}
              onClick={() => {
                props.onUseClick();

                const bokfDate = new Date(
                  new Date(bokforderDate ?? new Date()).setDate(1)
                );

                const fromDateObj = new Date(
                  props.fromDateInputValue.value ?? new Date()
                );

                const toDateObj = new Date(
                  props.toDateInputValue.value ?? new Date()
                );

                const diffDays = Math.abs(diffDay(fromDateObj, toDateObj));

                if (fromDateObj < bokfDate && toDateObj > bokfDate) {
                  props.setInfoSummeringDialogOpen(true);
                }

                if (diffDays > 365) {
                  props.setInfoDiffMoreThanOneYearDialogOpen(true);
                }
              }}
              testId="AnvandPeriodiseringButton"
            />

            <EDButton
              disabled={props.disableClearButton}
              disabledReason={props.clearButtonDisabledReason}
              label="Rensa"
              onClick={props.onClearClick}
            />
          </EDBox>
        </EDColumn>
      </EDColumns>
      <BokforderPeriodSummeringInfoDialog
        bokforderDate={formContext?.formState.currentValues?.BOKF_DATUM}
        fromDateString={props.fromDateInputValue.value}
        open={
          props.infoSummeringDialogOpen &&
          !props.infoDiffMoreThanOneYearDialogOpen
        }
        setOpen={props.setInfoSummeringDialogOpen}
      />
      <EDDialog
        footerActions={[
          {
            label: 'Ok',
            onClick: () => {
              props.setInfoDiffMoreThanOneYearDialogOpen(false);
            },
          },
        ]}
        open={props.infoDiffMoreThanOneYearDialogOpen}
        onClose={() => props.setInfoDiffMoreThanOneYearDialogOpen(false)}
        title={'Information om periodisering'}
      >
        <EDBox component="span">
          Perioden mellan från och till datum är mer än ett år
        </EDBox>
      </EDDialog>
    </>
  );
};

export default BokforderPeriodDateHandler;
